/** Client-only nav bar color hydration (DOM). Re-exports SSR helpers from `-shared`. */

export * from './nav-bar-background-shared';

import {
  buildNavBarBackgroundCss,
  coerceNavBarColorSettings,
  normalizeNavBarBackgroundColor,
  STOREFRONT_MENU_HOVER_BG_VAR,
  STOREFRONT_NAV_BAR_BG_VAR,
  STOREFRONT_SUBMENU_BG_VAR,
  STOREFRONT_SUBMENU_HOVER_BG_VAR,
  type StorefrontNavBarColorSettings,
} from './nav-bar-background-shared';

const NAV_BAR_SELECTOR = '[data-storefront-nav-bar="true"]';
const NAV_BAR_BG_STYLE_SELECTOR = 'style[data-storefront-nav-bar-bg-style="true"]';

function readColorFromBarDataset(
  bar: HTMLElement,
  datasetKey: 'navBarBackground' | 'submenuBackground' | 'menuHoverBackground' | 'submenuHoverBackground',
  cssVar: string,
): string {
  const fromData = normalizeNavBarBackgroundColor(bar.dataset[datasetKey]);
  if (fromData) return fromData;

  const fromVar = normalizeNavBarBackgroundColor(bar.style.getPropertyValue(cssVar));
  if (fromVar) return fromVar;

  return '';
}

/** Read colors already present on the SSR nav bar shell (avoid clearing on hydrate). */
export function readNavBarColorsFromElement(bar: HTMLElement): StorefrontNavBarColorSettings {
  const styleAttr = bar.getAttribute('style') ?? '';

  const readFromStyleAttr = (varName: string): string => {
    const match = styleAttr.match(new RegExp(`${varName}\\s*:\\s*(#[0-9a-fA-F]{3,6})`, 'i'));
    return match?.[1] ? normalizeNavBarBackgroundColor(match[1]) : '';
  };

  return {
    navBarBackgroundColor:
      readColorFromBarDataset(bar, 'navBarBackground', STOREFRONT_NAV_BAR_BG_VAR)
      || readFromStyleAttr(STOREFRONT_NAV_BAR_BG_VAR)
      || normalizeNavBarBackgroundColor(bar.style.backgroundColor),
    submenuBackgroundColor:
      readColorFromBarDataset(bar, 'submenuBackground', STOREFRONT_SUBMENU_BG_VAR)
      || readFromStyleAttr(STOREFRONT_SUBMENU_BG_VAR),
    menuHoverBackgroundColor:
      readColorFromBarDataset(bar, 'menuHoverBackground', STOREFRONT_MENU_HOVER_BG_VAR)
      || readFromStyleAttr(STOREFRONT_MENU_HOVER_BG_VAR),
    submenuHoverBackgroundColor:
      readColorFromBarDataset(bar, 'submenuHoverBackground', STOREFRONT_SUBMENU_HOVER_BG_VAR)
      || readFromStyleAttr(STOREFRONT_SUBMENU_HOVER_BG_VAR),
  };
}

/** @deprecated Use readNavBarColorsFromElement */
export function readNavBarBackgroundFromElement(bar: HTMLElement): string {
  return readNavBarColorsFromElement(bar).navBarBackgroundColor ?? '';
}

function syncNavBarBackgroundStyleSheet(headerMount: HTMLElement, css: string): void {
  if (!css) {
    clearNavBarBackgroundStyleSheet(headerMount);
    return;
  }

  let styleEl = headerMount.querySelector(NAV_BAR_BG_STYLE_SELECTOR);
  if (styleEl instanceof HTMLStyleElement) {
    styleEl.textContent = css;
    return;
  }
  styleEl = document.createElement('style');
  styleEl.dataset.storefrontNavBarBgStyle = 'true';
  styleEl.textContent = css;
  headerMount.insertBefore(styleEl, headerMount.firstChild);
}

function clearNavBarBackgroundStyleSheet(headerMount: HTMLElement): void {
  headerMount.querySelector(NAV_BAR_BG_STYLE_SELECTOR)?.remove();
}

function applyColorDataset(
  bar: HTMLElement,
  datasetKey: 'navBarBackground' | 'submenuBackground' | 'menuHoverBackground' | 'submenuHoverBackground',
  cssVar: string,
  color: string,
): void {
  if (color) {
    bar.dataset[datasetKey] = color;
    bar.style.setProperty(cssVar, color);
  } else {
    bar.style.removeProperty(cssVar);
    delete bar.dataset[datasetKey];
  }
}

export function applyNavBarBackgroundToMount(
  headerMount: HTMLElement,
  settingsOrNav?: StorefrontNavBarColorSettings | unknown,
  submenuBackgroundColor?: unknown,
  menuHoverBackgroundColor?: unknown,
  submenuHoverBackgroundColor?: unknown,
): void {
  const settings = coerceNavBarColorSettings(
    settingsOrNav,
    submenuBackgroundColor,
    menuHoverBackgroundColor,
    submenuHoverBackgroundColor,
  );
  const bar = headerMount.querySelector(NAV_BAR_SELECTOR);
  if (!(bar instanceof HTMLElement)) {
    return;
  }

  const existing = readNavBarColorsFromElement(bar);
  const resolved: StorefrontNavBarColorSettings = {
    navBarBackgroundColor:
      normalizeNavBarBackgroundColor(settings.navBarBackgroundColor) || existing.navBarBackgroundColor,
    submenuBackgroundColor:
      normalizeNavBarBackgroundColor(settings.submenuBackgroundColor) || existing.submenuBackgroundColor,
    menuHoverBackgroundColor:
      normalizeNavBarBackgroundColor(settings.menuHoverBackgroundColor) || existing.menuHoverBackgroundColor,
    submenuHoverBackgroundColor:
      normalizeNavBarBackgroundColor(settings.submenuHoverBackgroundColor)
      || existing.submenuHoverBackgroundColor,
  };

  const hasAnyColor = Object.values(resolved).some((color) => Boolean(color));
  if (!hasAnyColor) {
    clearNavBarBackgroundStyleSheet(headerMount);
    bar.style.removeProperty(STOREFRONT_NAV_BAR_BG_VAR);
    bar.style.removeProperty(STOREFRONT_SUBMENU_BG_VAR);
    bar.style.removeProperty(STOREFRONT_MENU_HOVER_BG_VAR);
    bar.style.removeProperty(STOREFRONT_SUBMENU_HOVER_BG_VAR);
    bar.style.removeProperty('background-color');
    delete bar.dataset.navBarBackground;
    delete bar.dataset.submenuBackground;
    delete bar.dataset.menuHoverBackground;
    delete bar.dataset.submenuHoverBackground;
    return;
  }

  const navColor = resolved.navBarBackgroundColor ?? '';
  if (navColor) {
    bar.dataset.navBarBackground = navColor;
    bar.style.setProperty(STOREFRONT_NAV_BAR_BG_VAR, navColor);
    bar.style.setProperty('background-color', navColor, 'important');
  } else {
    bar.style.removeProperty(STOREFRONT_NAV_BAR_BG_VAR);
    bar.style.removeProperty('background-color');
    delete bar.dataset.navBarBackground;
  }

  applyColorDataset(bar, 'submenuBackground', STOREFRONT_SUBMENU_BG_VAR, resolved.submenuBackgroundColor ?? '');
  applyColorDataset(bar, 'menuHoverBackground', STOREFRONT_MENU_HOVER_BG_VAR, resolved.menuHoverBackgroundColor ?? '');
  applyColorDataset(
    bar,
    'submenuHoverBackground',
    STOREFRONT_SUBMENU_HOVER_BG_VAR,
    resolved.submenuHoverBackgroundColor ?? '',
  );

  syncNavBarBackgroundStyleSheet(headerMount, buildNavBarBackgroundCss(resolved));
}
